import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { AttributeMapping } from '../../../../components/AttributeMapping';
import { useLocalization } from '../../../../hooks';
import { type RootState } from '../../../../services/serviceTypes';
import { useFetchCategoriesTemplateQuery } from '../../../Copilot1/data-store/disconnectedOnboardingApi';
import { type StepFormMessage } from '../../data-layer/types';
import { useFetchAttributesDataQuery } from '../../data-store/copilotApi';
import useRemoveAccessLogic from '../../hooks/removeAccessLogic';
import { useChatFlowController } from '../../hooks/useChatFlowController';
import ErrorBanner from '../ErrorBanner/ErrorBanner';
import ExpandableForm from '../ExpandableForm/ExpandableForm';
import Responses from '../Responses/Responses';

interface RemoveAccessAttributeMappingProps {
  message: StepFormMessage;
  onFormSubmit: (
    stepName: string,
    formData: Record<string, unknown>,
    isValid: boolean
  ) => void;
}

/**
 * Remove Access Attributemapping
 */
function RemoveAccessAttributeMapping({
  message,
  onFormSubmit,
}: RemoveAccessAttributeMappingProps) {
  const { handleSkip } = useChatFlowController();
  const formatMessage = useLocalization();
  const entitlementIndex = (message.formData.entitlementIndex as number) || 0;
  const [isSkipped, setIsSkipped] = useState(false);

  // Remove savingSteps dependency - buttons will be managed by form state
  const isSaving = false;

  const { revertedSteps, endpointKey } = useSelector(
    (state: {
      copilot: {
        revertedSteps?: Record<string, number>;
        endpointKey?: number;
      };
    }) => state.copilot
  );

  // Get endpointKey from Redux or URL
  const getEndpointKey = () => {
    let key = endpointKey;

    if (!key) {
      const urlParams = new URLSearchParams(window.location.search);
      const endpointKeyFromUrl = urlParams.get('endpointKey');

      if (endpointKeyFromUrl) {
        key = parseInt(endpointKeyFromUrl, 10);
      }
    }

    return key;
  };

  const {
    allValidationErrors,
    applicationName,
    entitlements,
    mandatoryMappings,
    handleMappingsChange,
    createDynamicConfig,
    formatEntitlementsList,
    getInitialMappings,
    handleNext,
  } = useRemoveAccessLogic();

  const validationError = allValidationErrors[entitlementIndex] || null;
  const initialMappings = getInitialMappings(entitlementIndex);

  const stepData = message.fields[0] as
    | {
        apiURL?: string;
        domainObjects?: string[];
        attributeDefaultValue?: string;
      }
    | undefined;

  const queryResult = useFetchAttributesDataQuery(
    {
      apiURL: stepData?.apiURL ?? '',
      domainObjects: stepData?.domainObjects ?? [],
    },
    {
      skip: !stepData?.apiURL || !stepData.domainObjects,
    }
  );

  const dynamicConfig = createDynamicConfig(
    queryResult.data as
      | { tableArributes?: Record<string, { name: string }[]> }
      | undefined
  );
  const isEditMode = useSelector(
    (state: { copilot: { ui: { mode: string } } }) =>
      state.copilot.ui.mode === 'edit'
  );

  // Check if step is completed
  const isCompletedCheck = useSelector(
    (state: RootState) => state.copilot.navigation.completedSteps ?? {}
  );
  const btnlabel = isCompletedCheck.includes(message.stepName ?? '');

  // Call API only in edit mode to update Redux state
  useFetchCategoriesTemplateQuery(
    {
      parentCategoryName: 'configuration',
      categoryNames: ['General', 'Connection', 'Provisioning'],
      applicationType: 'CUA',
      endpointKey: getEndpointKey(),
    },
    {
      skip: !isEditMode,
    }
  );

  const handleNextClick = () => {
    const template = stepData?.attributeDefaultValue ?? '';

    handleNext(
      entitlementIndex,
      template,
      onFormSubmit as (
        stepName: string,
        formData: Record<string, unknown>,
        isValid: boolean
      ) => void
    );
  };

  const handleFormSkip = () => {
    setIsSkipped(true);
    handleSkip('RemoveAccess', 'RemoveAccessNext', entitlementIndex);
  };

  if (!dynamicConfig) {
    return <div>Loading...</div>;
  }

  const allEntitlements = formatEntitlementsList(entitlements);
  const currentEntitlement = entitlements[entitlementIndex]?.value || 'Unknown';

  if (isEditMode && entitlements.length === 0) {
    return <Responses type='removeAccessSkipped' />;
  }

  return (
    <div>
      {entitlementIndex === 0 && !isEditMode && (
        <Responses
          type='attributeMappingStart'
          values={[allEntitlements, currentEntitlement]}
        />
      )}
      {isEditMode ? (
        <Responses type='removeAccessEditInfo' />
      ) : (
        <Responses type='removeAccessMappingInfo' values={[applicationName]} />
      )}
      <ExpandableForm
        stepName={`RemoveAccess_${entitlementIndex}`}
        isCollapseable
        heading={`Remove Access for ${currentEntitlement}`}
        buttonLabel={(() => {
          if (btnlabel) {
            return 'Update';
          }

          if (isEditMode) {
            return formatMessage('INTEGRATIONS.BUTTON.UPDATE', 'Update');
          }

          return formatMessage('INTEGRATIONS.BUTTON.SAVE', 'Save');
        })()}
        buttonkind='filled'
        secondryButtonKind='outlined'
        secondryButtonLabel={
          !isEditMode
            ? formatMessage('INTEGRATIONS.COPILOT.SKIP', 'Skip')
            : undefined
        }
        onSecondory={!isEditMode ? handleFormSkip : undefined}
        isDisabled={isSkipped || isSaving}
        entitlementIndex={entitlementIndex}
        onSave={handleNextClick}>
        <AttributeMapping
          key={`${entitlementIndex}-${
            revertedSteps?.[`RemoveAccess_${entitlementIndex}`] ?? 0
          }`}
          config={dynamicConfig}
          mandatoryMappings={
            initialMappings.length === 0 ? mandatoryMappings : []
          }
          initialMappings={initialMappings}
          onMappingsChange={(newMappings) => {
            handleMappingsChange(entitlementIndex, newMappings);
          }}
          showSaveButton={false}
        />
      </ExpandableForm>
      {validationError && <ErrorBanner message={validationError} />}
    </div>
  );
}

export default RemoveAccessAttributeMapping;
